import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { ApiError } from '@/api/client';
import { toast } from '@/stores/toast';
import { breadcrumb } from '@/lib/sentry';
import { shellT } from '@/i18n/messages/shell';
import { showsGlobalErrorToast } from '@/lib/mutationMeta';

// Retry policy:
//  - 4xx (validação, permissão, conflito) — não retenta. Re-tentar 403
//    queima rate-limit + tempo sem chance de sucesso. 422 idem.
//  - 408/425/429 — transitórios mas com sinalização explícita: o cliente
//    deve respeitar `Retry-After` se vier; aqui só damos 1 retry.
//  - 5xx ou network error — até 2 retries com backoff exponencial.
// Mutations herdam essa policy mas com retry=0 por default — DQ/DNF/reset
// são intent destrutivo e operador deve re-clicar conscientemente.

function shouldRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError) {
    if (error.status === 408 || error.status === 425 || error.status === 429) {
      return failureCount < 1;
    }
    if (error.status >= 400 && error.status < 500) return false;
  }
  return failureCount < 2;
}

type ApiErrorCopy = ReturnType<typeof shellT>['apiError'];

/// Extrai mensagem amigável de qualquer erro vindo do `api()` wrapper.
/// Prioriza `body.message` (vindo do FirebaseExceptionHandler global), depois
/// `body.error`, senão a `Error.message` padrão (que já contém status + body).
///
/// `t` é o idioma do título: o toast segue o idioma da tela, o breadcrumb fica
/// sempre em português (log não se traduz; quem filtra o Sentry procura o
/// mesmo texto, qualquer que seja o idioma de quem teve o erro). O corpo cru
/// do servidor vai como veio nos dois.
function describeError(
  error: unknown,
  t: ApiErrorCopy = shellT().apiError,
): { title: string; description?: string } {
  if (error instanceof ApiError) {
    const body = error.body as Record<string, unknown> | string | undefined;
    if (body && typeof body === 'object') {
      const message = typeof body.message === 'string' ? body.message : undefined;
      const code =
        typeof body.firebaseCode === 'string'
          ? body.firebaseCode
          : typeof body.error === 'string'
            ? body.error
            : undefined;
      if (message) {
        return { title: code ?? t.status(error.status), description: message };
      }
      if (code) {
        return { title: t.status(error.status), description: code };
      }
    }
    if (typeof body === 'string' && body.length > 0) {
      return { title: t.status(error.status), description: body };
    }
    return { title: t.statusWithText(error.status, error.statusText) };
  }
  if (error instanceof Error) {
    return { title: t.network, description: error.message };
  }
  return { title: t.unexpected };
}

/// Título do erro pro breadcrumb: sempre em português (ver `describeError`).
function breadcrumbTitle(error: unknown): string {
  return describeError(error, shellT.all.pt.apiError).title;
}

/// MutationCache global — captura erros de mutations que não definiram
/// `onError` próprio. Caso a mutation TENHA `onError` definido localmente,
/// esse callback global ainda dispara (TanStack chama os dois), o que é OK:
/// significa que o componente pode customizar UX *adicional* (banner inline)
/// e o toast global sinaliza "algo errado aconteceu" pra qualquer fluxo.
/// Exceção opt-in: `meta: INLINE_ERROR_META` (`lib/mutationMeta.ts`), pra
/// mutation que já mostra o erro do seu jeito: no campo do formulário ou num
/// toast próprio, com o código traduzido (exclusões e decisões de inscrição).
/// O breadcrumb sai do mesmo jeito.
const mutationCache = new MutationCache({
  onError: (error, _vars, _ctx, mutation) => {
    if (showsGlobalErrorToast(mutation.options.meta)) {
      const { title, description } = describeError(error);
      toast.error(title, description);
    }
    breadcrumb('mutation error', 'react-query', {
      mutationKey: mutation.options.mutationKey?.join('/') ?? 'unknown',
      message: breadcrumbTitle(error),
    });
  },
});

/// QueryCache global — mesmo princípio pra queries. Queries que mostram
/// `isError` inline (ex: Events.tsx list query) ficam com UX duplicada
/// (toast + banner). Aceitável: melhor sinalizar duas vezes que esconder
/// quando uma das duas falha de renderizar.
///
/// Mesma exceção opt-in das mutations (`meta: INLINE_ERROR_META`), pra query
/// informativa em polling: a lista de rastreadores do painel ao vivo (#1172)
/// é relida a cada 10 s, e uma falha dela viraria um toast a cada 10 s no meio
/// da prova, sobre um dado de que o resto da tela não depende. O breadcrumb
/// sai do mesmo jeito.
const queryCache = new QueryCache({
  onError: (error, query) => {
    if (showsGlobalErrorToast(query.meta)) {
      const { title, description } = describeError(error);
      toast.error(title, description);
    }
    breadcrumb('query error', 'react-query', {
      queryKey: query.queryKey.join('/'),
      message: breadcrumbTitle(error),
    });
  },
});

export const queryClient = new QueryClient({
  mutationCache,
  queryCache,
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: shouldRetry,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8_000),
    },
    mutations: {
      retry: false,
    },
  },
});
